// WelcomeSection.jsx
import React, { useEffect, useRef, useState } from 'react';
import { 
  FaHotel, 
  FaUserCheck, 
  FaStar, 
  FaCoffee, 
  FaWifi, 
  FaParking, 
  FaShieldAlt,
  FaArrowRight,
  FaQuoteLeft,
  FaCalendarCheck,
  FaSpa,
  FaUtensils,
  FaSwimmingPool,
  FaDumbbell,
  FaClock,
  FaHeart,
  FaCamera,
  FaConciergeBell
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const WelcomeSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const amenities = [
    { icon: FaWifi, name: 'Free WiFi', available: true },
    { icon: FaParking, name: 'Free Parking', available: true },
    { icon: FaShieldAlt, name: '24/7 Security', available: true },
    { icon: FaSpa, name: 'Luxury Spa', available: true },
  ];

  return (
    <section ref={sectionRef} className="py-16 px-4 bg-gradient-to-br from-white via-blue-50/30 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto">
        
        {/* Section Header with Animation - Blue */}
        <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider bg-blue-100/80 backdrop-blur-sm px-4 py-1.5 rounded-full inline-block mb-3 animate-pulse-slow">
            Welcome to Paridhi
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
            Luxury & Comfort <span className="text-blue-600">Redefined</span>
          </h2>
          <div className="w-20 h-1 bg-blue-500 mx-auto rounded-full animate-glow"></div>
          <p className="text-gray-600 max-w-2xl mx-auto mt-4">
            Experience the perfect blend of traditional hospitality and modern amenities at Paridhi Guest House
          </p>
        </div>

        {/* Left-Right Equal Layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Side - Image */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="relative group">
              <div className="rounded-2xl overflow-hidden shadow-2xl relative">
                <img
                  src="image3.png"
                  alt="Paridhi Guest House Luxury Room"
                  className="w-full h-[350px] md:h-[450px] object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-blue-900/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
              <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-lg animate-float">
                <div className="flex items-center gap-1">
                  <FaHeart className="text-red-500 text-xs animate-pulse" />
                  <span className="text-xs font-semibold text-gray-700">Guest Favorite</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Content */}
          <div className={`space-y-6 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            {/* Title - Orange */}
            <h3 className="text-2xl md:text-3xl font-bold text-orange-600 relative inline-block group">
              Paridhi Guest House
              <span className="absolute bottom-0 left-0 w-0 h-1 bg-orange-500 transition-all duration-500 group-hover:w-full"></span>
            </h3>
            <p className="text-gray-600 leading-relaxed animate-fadeIn">
              Paridhi Guest House offers a perfect blend of traditional warmth and contemporary luxury. 
              Nestled in the heart of Varanasi, we provide travelers with an unforgettable experience 
              that combines comfort, convenience, and cultural richness.
            </p>
            <p className="text-gray-600 leading-relaxed animate-fadeIn animation-delay-200">
              Each of our rooms is thoughtfully designed with modern amenities, premium bedding, 
              and elegant furnishings. Whether you're here for business or leisure, our dedicated team 
              ensures your stay is nothing short of perfect.
            </p>

            {/* Amenities Grid - Blue Icons */}
            <div className="grid grid-cols-2 gap-3">
              {amenities.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-2 p-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105 cursor-pointer group"
                >
                  <item.icon className="text-blue-500 text-sm transition-all duration-300 group-hover:text-blue-600 group-hover:scale-110" />
                  <span className="text-sm text-gray-700 group-hover:text-blue-600 transition-colors duration-300">{item.name}</span>
                </div>
              ))}
            </div>

            {/* Buttons - Blue Button, Orange Outline Button */}
            <div className="flex flex-wrap gap-4 pt-4">
              <Link
                to="/rooms"
                className="group relative inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2.5 rounded-lg font-semibold overflow-hidden transition-all shadow-md hover:shadow-xl hover:bg-blue-700"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Explore Rooms
                  <FaArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border-2 border-orange-500 text-orange-500 px-6 py-2.5 rounded-lg font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 transform hover:scale-105"
              >
                Contact Us
                <FaConciergeBell size={14} />
              </Link>
            </div>

            {/* Trust Badge - Orange Stars, Blue Trust Badge */}
            <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className="text-orange-400 text-xs animate-twinkle" style={{ animationDelay: `${i * 0.1}s` }} />
                ))}
                <span className="text-sm font-semibold text-gray-700 ml-1">4.8</span>
                <span className="text-xs text-gray-500">(2.5k reviews)</span>
              </div>
              <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-full">
                <FaShieldAlt className="text-blue-500 text-xs" />
                <span className="text-xs text-blue-600 font-medium">Trusted</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Features Bar - Blue Cards, Orange Hover */}
        <div className={`mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {[
            { icon: FaClock, title: '24/7 Support', desc: 'Always here for you', delay: 0 },
            { icon: FaCalendarCheck, title: 'Easy Booking', desc: 'Instant confirmation', delay: 100 },
            { icon: FaStar, title: 'Best Price', desc: 'Direct booking benefits', delay: 200 },
            { icon: FaShieldAlt, title: 'Safe Stay', desc: 'Hygiene certified', delay: 300 },
          ].map((item, index) => (
            <div 
              key={index}
              className="group text-center p-4 rounded-xl bg-white shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer"
              style={{ animationDelay: `${item.delay}ms` }}
            >
              <div className="relative w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2 transition-all duration-500 group-hover:bg-orange-500">
                <item.icon className="text-blue-600 text-xl transition-all duration-500 group-hover:text-white group-hover:scale-110" />
                <div className="absolute inset-0 rounded-full bg-orange-400 opacity-0 group-hover:opacity-30 group-hover:scale-150 transition-all duration-500"></div>
              </div>
              <p className="font-semibold text-gray-800 text-sm group-hover:text-orange-600 transition-colors duration-300">{item.title}</p>
              <p className="text-xs text-gray-500 group-hover:text-gray-600 transition-colors duration-300">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Offer Badge - Blue and Orange Separate */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-50 px-6 py-3 rounded-full shadow-md border border-orange-200">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-700">Limited Time Offer: </span>
            <span className="text-sm font-bold text-orange-600">Up to 30% Off on Direct Booking</span>
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes glow {
          0% { box-shadow: 0 0 5px rgba(59,130,246,0.3); }
          50% { box-shadow: 0 0 20px rgba(59,130,246,0.6); }
          100% { box-shadow: 0 0 5px rgba(59,130,246,0.3); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes twinkle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.9); }
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
        
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s linear infinite;
        }
        
        .animate-glow {
          animation: glow 2s ease-in-out infinite;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out forwards;
        }
        
        .animate-twinkle {
          animation: twinkle 1.5s ease-in-out infinite;
        }
        
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
        
        .animation-delay-400 {
          animation-delay: 0.4s;
        }
      `}</style>
    </section>
  );
};

export default WelcomeSection;